import React, { useState } from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle, 
  Box, 
  Typography, 
  useTheme, 
  Paper
} from '@mui/material';
import { SnackbarContent } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ExpandableErrorSnackbarProps {
  id: string | number;
  message: string;
  fullErrorDetails: string;
  dismissSnackbar: () => void;
}

const ExpandableErrorSnackbar = React.forwardRef<HTMLDivElement, ExpandableErrorSnackbarProps>(
  ({ id, message, fullErrorDetails, dismissSnackbar }, ref) => {
    const [open, setOpen] = useState(false);
    const theme = useTheme();

    const handleOpen = () => {
      setOpen(true);
    };

    const handleClose = () => {
      setOpen(false);
    };

    return (
      <SnackbarContent ref={ref}>
        <Box
          sx={{
            display: 'flex',
            backgroundColor: 'error.main',
            color: 'error.contrastText',
            padding: 2,
            borderRadius: 1,
            alignItems: 'center',
            boxShadow: 3,
            maxWidth: '600px',
          }}
        >
          <InfoIcon sx={{ marginRight: 1.5 }} />
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            {message}
          </Typography>
          <Button 
            color="inherit" 
            size="small" 
            onClick={handleOpen}
            sx={{ 
              marginLeft: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              whiteSpace: 'nowrap'
            }}
          >
            View Details
          </Button>
          <CloseIcon
            onClick={dismissSnackbar}
            sx={{ 
              marginLeft: 1,
              cursor: 'pointer',
              opacity: 0.7,
              '&:hover': { opacity: 1 }
            }}
          />
        </Box>

        <Dialog 
          open={open} 
          onClose={handleClose} 
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: theme.shape.borderRadius,
              boxShadow: theme.shadows[10]
            }
          }}
        >
          <DialogTitle 
            sx={{ 
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              display: 'flex',
              alignItems: 'center',
              padding: theme.spacing(2, 3)
            }}
          >
            <ErrorOutlineIcon sx={{ marginRight: theme.spacing(1) }} />
            Configuration Error Details
          </DialogTitle>
          <DialogContent 
            dividers
            sx={{ 
              padding: theme.spacing(3),
              backgroundColor: theme.palette.background.default
            }}
          >
            <Typography 
              variant="subtitle1" 
              color="text.secondary" 
              gutterBottom
            >
              The following error was detected in the configuration:
            </Typography>
            <Paper 
              elevation={0}
              sx={{ 
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? theme.palette.grey[900] 
                  : theme.palette.grey[100],
                padding: theme.spacing(2),
                borderRadius: theme.shape.borderRadius,
                overflowX: 'auto',
                border: `1px solid ${theme.palette.divider}`,
                color: theme.palette.error.main,
                maxHeight: '400px',
                overflowY: 'auto'
              }}
            >
              {fullErrorDetails}
            </Paper>
          </DialogContent>
          <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
            <Button 
              onClick={handleClose} 
              variant="contained" 
              color="primary"
              sx={{ minWidth: '100px' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </SnackbarContent>
    );
  }
);

export default ExpandableErrorSnackbar; 